import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { Button, ModalButtonWithForm } from 'components/common';
import { Icon } from 'components/common/icons';
import { FormGroup, RadioButtonCard, RadioGroup } from 'components/common/form';
import { UsersExportExcelOptions, UsersListSelectedUsers } from '.';

import type { FormApi } from 'informed';
import type { GenerateExcelExportReq } from 'shared-types/requests';
import type { SelectedUser } from './UsersListSelectedUsers';

export type ExporExcelFormState = Omit<GenerateExcelExportReq, 'userIds'>;

const UsersExportExcel = () => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { usersCount },
    serverConfig,
    theme
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [validColumnSelection, setValidColumnSelection] = useState(true);
  const [view, setView] = useState<'initial' | 'options'>('initial');
  const [visible, setVisible] = useState(false);

  const handleGoBack = () => {
    setView('initial');
  };

  const handleHide = () => {
    setVisible(false);
    setUsers([]);
    setView('initial');
  };

  const handleTypeChange = () => {
    formApi.current?.setValue('productAllOrSelected', 'all');
    formApi.current?.setValue('productIds', []);
  };

  const handleSubmit = async (values: ExporExcelFormState) => {
    if (view === 'initial') {
      setView('options');
      return false;
    }

    try {
      const response = await api.post(
        '/admin/analytics/excel-exports',
        {
          ...values,
          language,
          userIds: users.map((user) => user.id)
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const filename = `${`${serverConfig.hostname}_${values.type}`.replace(
        /(-|\.)/g,
        '_'
      )}.xlsx`;

      // @ts-ignore
      if (window.navigator.msSaveBlob) {
        // download file in ie
        // @ts-ignore
        window.navigator.msSaveBlob(blob, filename);
      } else {
        // download file by simulating a link click
        const fileUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const typeOptions = [
    {
      icon: 'user',
      label: messages.commonUsers,
      value: 'users'
    },
    ...(serverConfig.platformCompetenciesOnly
      ? []
      : [
          {
            icon: 'history',
            label: messages.profileCompletionHistory,
            value: 'completion-history'
          }
        ]),
    ...(serverConfig.hideCoursesFromUI
      ? []
      : [
          {
            icon: 'book',
            label: messages.commonCourses,
            value: 'courses'
          }
        ]),
    ...(serverConfig.hidePathsFromUI
      ? []
      : [
          {
            icon: 'tasks',
            label: messages.commonPaths,
            value: 'paths'
          }
        ]),
    ...(serverConfig.hideAchievementsFromUI
      ? []
      : [
          {
            icon: 'check-circle',
            label: messages.commonAchievementsAlt,
            value: 'achievements'
          }
        ]),
    ...(serverConfig.hideCalendarEventsFromUI
      ? []
      : [
          {
            icon: ['far', 'calendar-days'],
            label: messages.commonCalendarEvents,
            value: 'calendar-events'
          }
        ])
  ];

  const initialValues = {
    completionStatus: 'all',
    productIds: [],
    productAllOrSelected: 'all',
    sortByPrimary: 'user.email',
    type: 'users'
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={!usersCount}
      buttonIcon="file-excel"
      buttonIsDropdown="true"
      buttonText={messages.commonExportToExcel}
      contentStyle={{ padding: 0 }}
      footerContent={
        view !== 'initial' ? (
          <Button
            category="light"
            icon="chevron-left"
            onClick={handleGoBack}
            style={{ float: 'left' }}
            text={messages.commonGoBack}
          />
        ) : null
      }
      initialValues={initialValues}
      modalActionText={
        view === 'initial'
          ? messages.usersExportExcelContinueOptions
          : messages.commonExportData
      }
      modalTitle={messages.commonExportToExcel}
      modalWidth="wide"
      onHide={handleHide}
      onShow={() => setVisible(true)}
      onSubmit={handleSubmit}
      setFormApi={(api: FormApi) => (formApi.current = api)}
      submitDisabled={!validColumnSelection}
    >
      {({ values }: { values: ExporExcelFormState }) => (
        <div>
          <div
            className="view"
            style={{ display: view === 'initial' ? 'block' : 'none' }}
          >
            {visible ? (
              <UsersListSelectedUsers
                allowSelf={true}
                onLoadedUsers={(users) => setUsers(users)}
              />
            ) : null}

            <div>
              <FormGroup
                htmlFor="title"
                label={messages.usersExportExcelChooseData}
                maxWidth="100%"
                style={{ marginTop: view === 'initial' ? 35 : 0 }}
              >
                <RadioGroup
                  id="export-excel-type"
                  field="type"
                  onChange={handleTypeChange}
                >
                  <div className="types">
                    {typeOptions.map((option) => (
                      <div className="type" key={option.value}>
                        <RadioButtonCard
                          id={`export-excel-type-${option.value}`}
                          field="type"
                          value={option.value}
                        >
                          <div className="type-inner">
                            <div className="type-icon">
                              <Icon
                                icon={['far', 'file-excel']}
                                style={{ position: 'relative', top: 1 }}
                              />
                            </div>

                            <div className="type-title">{option.label}</div>
                          </div>
                        </RadioButtonCard>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </FormGroup>
            </div>
          </div>

          <div
            className="type-header"
            style={{ display: view !== 'initial' ? 'block' : 'none' }}
          >
            <div className="type-inner">
              <div className="type-icon">
                <Icon icon={['far', 'file-excel']} />
              </div>

              <div className="type-title">
                {
                  typeOptions.find((option) => option.value === values?.type)
                    ?.label
                }
              </div>
            </div>
          </div>

          <div
            className="options-view"
            style={{ display: view === 'options' ? 'block' : 'none' }}
          >
            <UsersExportExcelOptions
              setValidColumnSelection={setValidColumnSelection}
            />
          </div>

          <style jsx>{`
            .view {
              padding: 15px;
            }
            .types {
              display: flex;
              flex-wrap: wrap;
              gap: 15px;
              margin-top: 10px;
            }
            .type {
              width: 100%;
            }
            .type-inner {
              display: flex;
              gap: 10px;
              padding: 5px;
            }
            .type-icon {
              color: #217346;
            }
            .type-title {
              color: ${theme.textColorLight};
              font-weight: 700;
            }
            .type-header {
              border-bottom: 1px solid ${theme.lightGrey};
              padding: 15px;
            }
            .options-view {
              padding: 15px 15px 30px;
            }
            @media (min-width: ${breakpoints.small}px) {
              .type {
                width: calc(50% - 7.5px);
              }
            }
            @media (min-width: ${breakpoints.medium}px) {
              .type {
                width: calc(33.333% - 10px);
              }
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersExportExcel;
